/* eslint-disable indent */
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { useIsWorkerClassificationEnabled, useRequestControls } from '~/selectors';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import { clearMultidayForm } from '../../../actions';
import { Row } from '../../../commonComponents';
import {
  EnforceOfficeDashboardV2,
  ShiftRequestRedesign,
  DashboardPermNewBadge,
} from '../../../constants/ff.posthog';
import { getShouldEnforcePermanentJobV2 } from '../../../growthbook';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useUserSelector } from '../../../hooks/useUserSelector';
import { Colors } from '../../../themes/colors';
import { CreatePostingButton } from '../../PermanentJobsV2';
import { PostNewShiftsButton } from '../../ShiftRequestRedesign/PostNewShiftsButton/PostNewShiftsButton';
import { fireDashboardPostJobClicked } from '../../../posthogs/events';
import { OfficeAuthorizedButton } from './Button';
import { ButtonContainer } from './components/ButtonContainer';

const Card = ({ children }) => <div className="dashboard-top-button">{children}</div>;

const Button = ({ disabled, text, clickUrl, isBeta, style, listener }) => (
  <div
    className="dashboard-buttons-shadow"
    style={{ flex: 0, height: 48, marginTop: '10px' }}
    onClick={() => {
      listener?.();
    }}
  >
    <Link
      to={clickUrl}
      style={{
        display: 'flex',
        textDecoration: 'none',
        height: '100%',
        width: 'auto',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: disabled ? 'none' : 'auto',
        backgroundColor: disabled ? Colors.primary_200 : Colors.primary_500,
        fontFamily: 'Nunito',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '18px',
        lineHeight: '25px',
        color: Colors.white,
        borderRadius: '32px',
        whiteSpace: 'nowrap',
        padding: '0 20px',
        ...style,
      }}
    >
      {text}
      {isBeta && (
        <p
          style={{
            fontSize: '10px',
            color: Colors.primary_500,
            backgroundColor: Colors.white,
            height: '16px',
            borderRadius: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 10px',
            marginLeft: '8px',
          }}
        >
          Beta
        </p>
      )}
    </Link>
  </div>
);

const TopButtons = ({ showLocumTenens = false }) => {
  const dispatch = useDispatch();
  const user = useUserSelector();
  const allowedRequest = user?.allowedRequest || {};
  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();
  const missingWorkerClassification =
    isWorkerClassificationEnabled && !user?.workerClassification?.classificationType;
  const useNewShiftsWidget = useFeatureFlagVariant('guided-user-journey-dashboard', 'test');
  const useOfficeDashboardV2 = useFeatureFlag(EnforceOfficeDashboardV2);
  const shouldUseShiftRequestRedesign = useFeatureFlag(ShiftRequestRedesign);
  const isEnforceDashboardPermNewBadge = useFeatureFlag(DashboardPermNewBadge);

  const { isPermProductAllowed, isPermanentPlacementAllowed, isTempOrMDBShiftAllowed } =
    useRequestControls();

  const hasPostingButton = isTempOrMDBShiftAllowed || isPermanentPlacementAllowed;

  useEffect(() => {
    dispatch(clearMultidayForm());
  }, [dispatch]);

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{
        flex: 1,
        padding: '0px 40px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <p
        style={{
          fontFamily: 'Nunito',
          fontWeight: 'bold',
          fontSize: 18,
          color: Colors.neutral_400,
          textAlign: 'center',
        }}
      >
        Temporary and Permanent Services are currently disabled. Please contact your account manager
        for support.
      </p>
    </motion.div>
  );

  if (useOfficeDashboardV2) {
    return hasPostingButton ? (
      <Row gap={20}>
        {isTempOrMDBShiftAllowed && (
          <ButtonContainer
            variant="teal"
            title="Need Coverage Fast?"
            description="Reach the largest network of verified professionals instantly."
            disabled={missingWorkerClassification}
            ButtonComponent={
              shouldUseShiftRequestRedesign ? (
                <PostNewShiftsButton
                  text="Post Shifts"
                  disabled={missingWorkerClassification}
                  style={{ width: 184, backgroundColor: Colors.white }}
                  textStyle={{
                    color: missingWorkerClassification ? Colors.primary_200 : Colors.primary_500,
                  }}
                />
              ) : (
                <OfficeAuthorizedButton
                  description={`Select or type the office name in the field below to post a shift at the correct location.`}
                  isDisabled={missingWorkerClassification}
                  text="Post Shifts"
                  targetUrl="/dashboard/multi-day/info"
                  style={{ width: 184, backgroundColor: Colors.white }}
                  textStyle={{
                    color: missingWorkerClassification ? Colors.primary_200 : Colors.primary_500,
                  }}
                  onTrackEvent={() => {
                    trackPosthogEvent(POSTHOG_EVENTS.OFFICE_DASHBOARD_SHIFT_REQUEST_CLICKED);
                  }}
                />
              )
            }
          />
        )}
        {isPermanentPlacementAllowed && (
          <ButtonContainer
            variant="green"
            title="Need Full-Time Staff?"
            description="Post a job to start finding your next full-time hire today."
            showNewBadge={
              isEnforceDashboardPermNewBadge &&
              getShouldEnforcePermanentJobV2() &&
              isPermProductAllowed
            }
            ButtonComponent={
              getShouldEnforcePermanentJobV2() && isPermProductAllowed ? (
                <CreatePostingButton
                  text="Post a Job"
                  style={{ width: 184, backgroundColor: Colors.white }}
                  textStyle={{ color: Colors.primary_500 }}
                  loadingColor="primary_500"
                  onTrack={(postingId) => {
                    // Import will be added at the top
                    fireDashboardPostJobClicked({ postingId });
                  }}
                />
              ) : (
                <OfficeAuthorizedButton
                  description="Select or type the office name in the field below to request a permanent hire at the correct location."
                  text="Post a Job"
                  targetUrl="/dashboard/pprs"
                  style={{ width: 184, backgroundColor: Colors.white }}
                  textStyle={{ color: Colors.primary_500 }}
                  onTrackEvent={() => {
                    trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_DASHBOARD_PERM_REQUEST_CLICKED);
                  }}
                />
              )
            }
          />
        )}
      </Row>
    ) : null;
  }

  return (
    <Card>
      {hasPostingButton || (allowedRequest?.locumTenensDentist && showLocumTenens) ? (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              height: 65,
              alignItems: 'center',
              columnGap: '14px',
            }}
          >
            {isTempOrMDBShiftAllowed &&
              (shouldUseShiftRequestRedesign ? (
                <PostNewShiftsButton
                  text={useNewShiftsWidget ? 'Post New Shifts' : 'Post A Shift'}
                  disabled={missingWorkerClassification}
                />
              ) : (
                <OfficeAuthorizedButton
                  description={`Select or type the office name in the field below to post a shift at the correct location.`}
                  isDisabled={missingWorkerClassification}
                  text={useNewShiftsWidget ? 'Post New Shifts' : 'Post A Shift'}
                  targetUrl="/dashboard/multi-day/info"
                  onTrackEvent={() => {
                    trackPosthogEvent(POSTHOG_EVENTS.OFFICE_DASHBOARD_SHIFT_REQUEST_CLICKED);
                  }}
                />
              ))}

            {isPermanentPlacementAllowed &&
              (getShouldEnforcePermanentJobV2() && isPermProductAllowed ? (
                <CreatePostingButton />
              ) : (
                <OfficeAuthorizedButton
                  description="Select or type the office name in the field below to request a permanent hire at the correct location."
                  text="Permanent Hire"
                  targetUrl="/dashboard/pprs"
                  onTrackEvent={() => {
                    trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_DASHBOARD_PERM_REQUEST_CLICKED);
                  }}
                />
              ))}

            {showLocumTenens && allowedRequest?.locumTenensDentist && (
              <Button
                text="Locum Tenens Dentist"
                clickUrl="/dashboard/locum-tenens-dentist"
                isBeta
              />
            )}
          </div>
        </>
      ) : (
        renderEmptyState()
      )}
    </Card>
  );
};

export default TopButtons;
